/**
 * CreateProjectPage
 * Single-screen, upload-first project creation.
 *
 * The philosophy: you drop in your document package and extraction fills in
 * everything else (name, contract number, parties, dates). So the page asks
 * for a type, a package, and — optionally — a name. Everything derivable is
 * left to the ingestion pipeline.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { DeliveryTypes } from '@mono/proto-clients';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraFormField, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowLeft, Briefcase, Crosshair, FileText, FolderOpen, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProgramAccessGate } from '../../components/access/ProgramAccessGate';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { FileUploader } from '../../components/ProjectList/FileUploader';
import { useCreateProject } from '../../hooks/useProjects';
import { deliveryClient } from '../../lib/connect/clients';
import { getProjectPath } from '../../lib/utils/project-routes';
const PROJECT_TYPES = [
    {
        type: 'general',
        label: 'General',
        description: 'Organize documents, chats, and workflows.',
        icon: FolderOpen
    },
    {
        type: 'proposal',
        label: 'Proposal',
        description: 'Drop a solicitation package — requirements and dates are extracted.',
        icon: FileText
    },
    {
        type: 'contract',
        label: 'Contract',
        description: 'Drop an awarded contract — CLINs, deliverables, and parties are extracted.',
        icon: Briefcase
    }
];
// Mirror of the dialog's contract-type list, without testids (this page is not
// driven by ui-cli). Kept short — the authoritative type is read from the
// documents; this is an override for incomplete packages.
const CONTRACT_TYPES = [
    {
        value: 'FFP',
        label: 'Firm Fixed Price (FFP)'
    },
    {
        value: 'T&M',
        label: 'Time & Materials (T&M)'
    },
    {
        value: 'CPFF',
        label: 'Cost Plus Fixed Fee (CPFF)'
    },
    {
        value: 'CPIF',
        label: 'Cost Plus Incentive Fee (CPIF)'
    },
    {
        value: 'CPAF',
        label: 'Cost Plus Award Fee (CPAF)'
    },
    {
        value: 'IDIQ',
        label: 'Indefinite Delivery/Indefinite Quantity (IDIQ)'
    },
    {
        value: 'BPA',
        label: 'Blanket Purchase Agreement (BPA)'
    },
    {
        value: 'HYBRID',
        label: 'Hybrid'
    },
    {
        value: 'OTHER',
        label: 'Other'
    }
];
const OPTIONAL = /*#__PURE__*/ _jsx("span", {
    className: "font-normal text-muted-foreground",
    children: "(optional)"
});
export function CreateProjectPage() {
    const navigate = useNavigate();
    const createProject = useCreateProject();
    const [type, setType] = useState('general');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState([]);
    const [contractNumber, setContractNumber] = useState('');
    const [contractType, setContractType] = useState('');
    const [touched, setTouched] = useState(false);
    const [accessGateProgramId, setAccessGateProgramId] = useState(null);
    const [resolving, setResolving] = useState(false);
    const hasPackage = type === 'proposal' || type === 'contract';
    const isNameRequired = type !== 'proposal';
    const isNameEmpty = name.trim().length === 0;
    const showNameError = touched && isNameEmpty && isNameRequired;
    const isValid = !isNameRequired || !isNameEmpty;
    const changeType = (next)=>{
        setType(next);
        if (next === 'general') setFiles([]);
        if (next !== 'contract') {
            setContractNumber('');
            setContractType('');
        }
    };
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setTouched(true);
        if (!isValid) return;
        const trimmedContractNumber = type === 'contract' ? contractNumber.trim() : '';
        if (trimmedContractNumber) {
            setResolving(true);
            try {
                const resp = await deliveryClient.resolveContractProgram({
                    contractNumber: trimmedContractNumber,
                    taskNumber: ''
                });
                if (resp.access === DeliveryTypes.ResolveContractProgramResponse_Access.VIEWABLE && resp.programId) {
                    navigate(`/delivery/programs/${resp.programId}`);
                    return;
                }
                if (resp.access === DeliveryTypes.ResolveContractProgramResponse_Access.NO_ACCESS && resp.programId) {
                    setAccessGateProgramId(resp.programId);
                    return;
                }
            } catch (err) {
                if (err instanceof ConnectError && err.code === Code.FailedPrecondition) {
                    // IDIQ base + task orders can share a contract number; without
                    // task_number we can't dedupe — fall through and create the project.
                    toast.message('Multiple programs share this contract number — creating a new project.');
                } else {
                    toast.error(err instanceof Error ? err.message : 'Could not check for an existing program');
                    return;
                }
            } finally{
                setResolving(false);
            }
        }
        createProject.mutate({
            name: name.trim() || undefined,
            description: description.trim() || undefined,
            type,
            files: files.length > 0 ? files : undefined,
            contractNumber: type === 'contract' ? contractNumber.trim() || undefined : undefined,
            contractType: type === 'contract' ? contractType || undefined : undefined
        }, {
            onSuccess: (data)=>{
                if (data.project) {
                    navigate(getProjectPath({
                        type,
                        projectId: data.project.projectId
                    }));
                }
            }
        });
    };
    if (accessGateProgramId) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ProgramAccessGate, {
                programId: accessGateProgramId,
                title: "This contract already exists",
                description: "A delivery program for this contract number is already in your organization, but you don't have access yet. Request access below.",
                backHref: "/projects/new",
                backLabel: "Back to create"
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "font-display text-[length:var(--fs-ui-title)] font-medium tracking-[var(--ls-display)] text-foreground",
                            children: "Create Project"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: "Drop in your document package — everything else is read from it."
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PageBody, {
                maxWidth: "xl",
                children: /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    className: "mx-auto max-w-2xl space-y-6",
                    children: [
                        /*#__PURE__*/ _jsxs("fieldset", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "mb-2 block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                    children: "Project type"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4",
                                    children: [
                                        PROJECT_TYPES.map((pt)=>{
                                            const selected = type === pt.type;
                                            return /*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                onClick: ()=>changeType(pt.type),
                                                "aria-pressed": selected,
                                                className: `rounded-lg border p-4 text-left transition-colors ${selected ? 'border-foreground bg-accent' : 'border-border bg-card hover:border-border-strong'}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx(pt.icon, {
                                                        className: "mb-2 h-5 w-5 text-muted-foreground"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "block text-sm font-medium text-foreground",
                                                        children: pt.label
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mt-1 block text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                                        children: pt.description
                                                    })
                                                ]
                                            }, pt.type);
                                        }),
                                        /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: ()=>navigate('/capture'),
                                            "data-testid": Testids.project.pursuitCta,
                                            className: "rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-border-strong",
                                            children: [
                                                /*#__PURE__*/ _jsx(Crosshair, {
                                                    className: "mb-2 h-5 w-5 text-muted-foreground"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "block text-sm font-medium text-foreground",
                                                    children: "Pursuit"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mt-1 block text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                                    children: "Shape a deal thread — workstreams, touchpoints, go/no-go. Not a General project."
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        hasPackage && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                    children: [
                                        "Document package ",
                                        OPTIONAL
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: type === 'proposal' ? 'Drop the solicitation package. We read the title, agency, and due dates from it.' : 'Drop the contract package. We read the name, number, parties, and dates from it.'
                                }),
                                /*#__PURE__*/ _jsx(FileUploader, {
                                    files: files,
                                    onChange: setFiles,
                                    disabled: createProject.isPending
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: isNameRequired ? 'Project name' : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Project name ",
                                    OPTIONAL
                                ]
                            }),
                            htmlFor: "project-name",
                            required: isNameRequired,
                            error: showNameError ? 'Project name is required' : undefined,
                            hint: type === 'proposal' && isNameEmpty ? 'A name will be generated from your solicitation documents.' : undefined,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "project-name",
                                value: name,
                                onChange: (e)=>setName(e.target.value),
                                onBlur: ()=>setTouched(true),
                                placeholder: isNameRequired ? 'e.g. NASA Cloud Services' : 'Auto-named from your documents',
                                maxLength: 200,
                                invalid: showNameError,
                                required: isNameRequired,
                                "aria-required": isNameRequired,
                                disabled: createProject.isPending
                            })
                        }),
                        type === 'contract' && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraFormField, {
                                    label: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            "Contract number ",
                                            OPTIONAL
                                        ]
                                    }),
                                    htmlFor: "contract-number",
                                    hint: "Read from your documents if left blank.",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "contract-number",
                                        value: contractNumber,
                                        onChange: (e)=>setContractNumber(e.target.value),
                                        placeholder: "e.g. GS-00F-0000X",
                                        disabled: createProject.isPending
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsxs("label", {
                                            htmlFor: "contract-type",
                                            className: "block text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                                            children: [
                                                "Contract type ",
                                                OPTIONAL
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(Select, {
                                            value: contractType,
                                            onValueChange: setContractType,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    id: "contract-type",
                                                    disabled: createProject.isPending,
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                                        placeholder: "Select contract type"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: CONTRACT_TYPES.map((ct)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: ct.value,
                                                            children: ct.label
                                                        }, ct.value))
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    "Description ",
                                    OPTIONAL
                                ]
                            }),
                            htmlFor: "project-description",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                id: "project-description",
                                value: description,
                                onChange: (e)=>setDescription(e.target.value),
                                placeholder: "Add context for your team",
                                rows: 3,
                                maxLength: 2000,
                                disabled: createProject.isPending
                            })
                        }),
                        createProject.isError && /*#__PURE__*/ _jsx("p", {
                            className: "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive",
                            role: "alert",
                            children: createProject.error instanceof Error ? createProject.error.message : 'Failed to create project. Please try again.'
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between border-t border-border pt-6",
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    type: "button",
                                    variant: "secondary",
                                    onClick: ()=>navigate('/projects'),
                                    disabled: createProject.isPending,
                                    children: [
                                        /*#__PURE__*/ _jsx(ArrowLeft, {
                                            className: "h-4 w-4"
                                        }),
                                        "Cancel"
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraButton, {
                                    type: "submit",
                                    isLoading: createProject.isPending || resolving,
                                    disabled: createProject.isPending || resolving || touched && !isValid,
                                    children: [
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-4 w-4"
                                        }),
                                        "Create Project"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
